// src/pages/admin/ManageProducts.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api, {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  fetchCategories,
} from "../../api/api";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputBase,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import EditIcon from "@mui/icons-material/Edit";
import GridViewIcon from "@mui/icons-material/GridView";
import ImageIcon from "@mui/icons-material/Image";
import InventoryIcon from "@mui/icons-material/Inventory";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import TableRowsIcon from "@mui/icons-material/TableRows";
import UploadIcon from "@mui/icons-material/Upload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

import { useSnackbar } from "../../utils/snackbar";

/* ─── constants ──────────────────────────────────────────────────────────── */
const BRAND_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const LOW_STOCK_THRESHOLD_DEFAULT = 5;

const STATUS_OPTIONS = [
  { value: "all", label: "All Products" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "low_stock", label: "Low Stock" },
  { value: "out_of_stock", label: "Out of Stock" },
  { value: "featured", label: "Featured" },
  { value: "flash_deal", label: "Flash Deal" },
];

const SORT_FIELDS = ["title", "price"];

/* ─── helpers ────────────────────────────────────────────────────────────── */
const safeNum = (v, fb = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
};

const formatKSh = (v) =>
  `KSh ${safeNum(v).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getStockStatus = (stock, threshold = LOW_STOCK_THRESHOLD_DEFAULT) => {
  const s = safeNum(stock);
  if (s === 0) return "out_of_stock";
  if (s <= threshold) return "low_stock";
  return "in_stock";
};

const stockChip = (stock, threshold) => {
  const status = getStockStatus(stock, threshold);
  if (status === "out_of_stock")
    return { label: "Out of Stock", color: "error" };
  if (status === "low_stock")
    return { label: "Low Stock", color: "warning" };
  return { label: "In Stock", color: "success" };
};

const descOrEmpty = (s) => (typeof s === "string" ? s : "");

/* ─── StatsChips ─────────────────────────────────────────────────────────── */
function StatsChips({ products, statusFilter, onFilter }) {
  const theme = useTheme();

  const stats = useMemo(() => {
    const total = products.length;
    const active = products.filter((p) => p.isActive !== false).length;
    const inactive = products.filter((p) => p.isActive === false).length;
    const lowStock = products.filter(
      (p) => getStockStatus(p.stock, p.lowStockThreshold) === "low_stock"
    ).length;
    const outOfStock = products.filter(
      (p) => getStockStatus(p.stock, p.lowStockThreshold) === "out_of_stock"
    ).length;
    const featured = products.filter((p) => p.featured === true).length;
    const flashDeal = products.filter((p) => p.isFlashDeal === true).length;
    return { total, active, inactive, lowStock, outOfStock, featured, flashDeal };
  }, [products]);

  const chips = [
    { key: "all", label: "Total", value: stats.total, color: "default" },
    { key: "active", label: "Active", value: stats.active, color: "success" },
    { key: "inactive", label: "Inactive", value: stats.inactive, color: "default" },
    { key: "low_stock", label: "Low Stock", value: stats.lowStock, color: "warning" },
    { key: "out_of_stock", label: "Out of Stock", value: stats.outOfStock, color: "error" },
    { key: "featured", label: "Featured", value: stats.featured, color: "primary" },
    { key: "flash_deal", label: "Flash Deal", value: stats.flashDeal, color: "error" },
  ];

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
      {chips.map((c) => {
        const active = statusFilter === c.key;
        return (
          <Chip
            key={c.key}
            label={`${c.label}: ${c.value}`}
            color={active ? (c.color === "default" ? "primary" : c.color) : "default"}
            variant={active ? "filled" : "outlined"}
            onClick={() => onFilter(c.key)}
            sx={{
              fontWeight: 700,
              cursor: "pointer",
              ...(active && c.color === "default" && {
                background: BRAND_GRADIENT,
                color: "#fff",
                border: "none",
              }),
            }}
          />
        );
      })}
    </Box>
  );
}

/* ─── MultiImageUploadArea ───────────────────────────────────────────────── */
function MultiImageUploadArea({ images, onChange, onUpload, uploading }) {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );
    files.forEach((f) => onUpload(f));
  };

  const handleFilesSelected = (e) => {
    Array.from(e.target.files).forEach((f) => onUpload(f));
    e.target.value = "";
  };

  const removeImage = (idx) => onChange(images.filter((_, i) => i !== idx));

  const handleImgDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleImgDragOver = (e, idx) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };

  const handleImgDrop = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...images];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    onChange(next);
    setDragIdx(null);
    setDragOverIdx(null);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
        {images.map((url, idx) => (
          <Box
            key={url + idx}
            draggable
            onDragStart={(e) => handleImgDragStart(e, idx)}
            onDragOver={(e) => handleImgDragOver(e, idx)}
            onDrop={(e) => handleImgDrop(e, idx)}
            onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
            sx={{
              width: 88,
              height: 88,
              borderRadius: 2,
              overflow: "hidden",
              border: `2px solid ${
                dragOverIdx === idx
                  ? theme.palette.primary.main
                  : theme.palette.divider
              }`,
              position: "relative",
              cursor: "grab",
              opacity: dragIdx === idx ? 0.45 : 1,
              transition: "opacity 0.15s, border-color 0.15s",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={url}
              alt={`product-${idx}`}
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            {idx === 0 && (
              <Chip
                label="Main"
                size="small"
                color="primary"
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  height: 16,
                  fontSize: 9,
                  fontWeight: 800,
                  "& .MuiChip-label": { px: 0.6 },
                }}
              />
            )}
            <IconButton
              size="small"
              onClick={() => removeImage(idx)}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "#fff",
                p: 0.25,
                "&:hover": { bgcolor: "rgba(200,0,0,0.8)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
            <DragIndicatorIcon
              sx={{
                position: "absolute",
                bottom: 2,
                left: "50%",
                transform: "translateX(-50%)",
                color: "rgba(255,255,255,0.65)",
                fontSize: 14,
                pointerEvents: "none",
              }}
            />
          </Box>
        ))}

        {/* Drop / add zone */}
        <Box
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleFileDrop}
          sx={{
            width: 88,
            height: 88,
            borderRadius: 2,
            border: `2px dashed ${
              dragOver ? theme.palette.primary.main : theme.palette.divider
            }`,
            bgcolor: dragOver
              ? alpha(theme.palette.primary.main, 0.07)
              : "background.default",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: uploading ? "wait" : "pointer",
            transition: "all 0.2s",
            flexShrink: 0,
            "&:hover": { borderColor: "primary.main" },
          }}
        >
          {uploading ? (
            <CircularProgress size={22} />
          ) : (
            <>
              <UploadIcon sx={{ color: "text.secondary", fontSize: 24 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.3, fontSize: 10, textAlign: "center", lineHeight: 1.2 }}
              >
                {images.length === 0 ? "Add\nimage" : "Add\nmore"}
              </Typography>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            hidden
            multiple
            accept="image/*"
            disabled={uploading}
            onChange={handleFilesSelected}
          />
        </Box>
      </Box>

      {images.length > 1 && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.8, display: "block" }}>
          Drag thumbnails to reorder · First image is the main display
        </Typography>
      )}
    </Box>
  );
}

/* ─── ProductDialog (Add / Edit) ─────────────────────────────────────────── */
function ProductDialog({ open, product, categories, onClose, onSaved, showAlert }) {
  const theme = useTheme();
  const isEdit = Boolean(product?._id);

  const emptyForm = {
    title: "",
    description: "",
    price: "",
    comparePrice: "",
    stock: "",
    lowStockThreshold: String(LOW_STOCK_THRESHOLD_DEFAULT),
    barcode: "",
    sku: "",
    isActive: true,
    categoryId: "",
    images: [],
    featured: false,
    featuredOrder: "",
    isFlashDeal: false,
    flashDiscountPercent: "20",
    flashStartAt: "",
    flashEndAt: "",
    flashDealOrder: "",
    promoLabel: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Populate form when product changes
  useEffect(() => {
    if (!open) return;
    if (product) {
      const catId =
        typeof product.category === "object"
          ? product.category?._id ?? ""
          : product.category ?? "";
      setForm({
        title: product.title ?? "",
        description: descOrEmpty(product.description),
        price: product.price != null ? String(product.price) : "",
        comparePrice: product.comparePrice != null ? String(product.comparePrice) : "",
        stock: product.stock != null ? String(product.stock) : "",
        lowStockThreshold:
          product.lowStockThreshold != null
            ? String(product.lowStockThreshold)
            : String(LOW_STOCK_THRESHOLD_DEFAULT),
        barcode: product.barcode ?? "",
        sku: product.sku ?? "",
        isActive: product.isActive !== false,
        categoryId: catId,
        images: product.images ?? [],
        featured: product.featured ?? false,
        featuredOrder: product.featuredOrder != null ? String(product.featuredOrder) : "",
        isFlashDeal: product.isFlashDeal ?? false,
        flashDiscountPercent: product.flashDeal?.discountPercent != null ? String(product.flashDeal.discountPercent) : "20",
        flashStartAt: product.flashDeal?.startAt ? new Date(product.flashDeal.startAt).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
        flashEndAt: product.flashDeal?.endAt ? new Date(product.flashDeal.endAt).toISOString().slice(0, 16) : "",
        flashDealOrder: product.flashDealOrder != null ? String(product.flashDealOrder) : "",
        promoLabel: product.promoLabel ?? "",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [open, product?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
    if (errors[field]) setErrors((err) => ({ ...err, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = "Title is required";
    if (form.price === "" || safeNum(form.price) <= 0)
      errs.price = "Price must be greater than 0";
    return errs;
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post("/api/upload", fd);
      const url = res.data?.url;
      if (!url) throw new Error("No URL returned");
      setForm((f) => ({ ...f, images: [...f.images, url] }));
    } catch (err) {
      showAlert("Image upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        price: safeNum(form.price),
        comparePrice: form.comparePrice !== "" ? safeNum(form.comparePrice) : undefined,
        stock: form.stock !== "" ? safeNum(form.stock) : 0,
        lowStockThreshold: safeNum(form.lowStockThreshold, LOW_STOCK_THRESHOLD_DEFAULT),
        barcode: form.barcode.trim() || undefined,
        sku: form.sku.trim() || undefined,
        isActive: form.isActive,
        category: form.categoryId || undefined,
        images: form.images,
        featured: form.featured,
        featuredOrder:
          form.featured && form.featuredOrder !== ""
            ? safeNum(form.featuredOrder)
            : undefined,
        isFlashDeal: form.isFlashDeal,
        flashDeal: form.isFlashDeal
          ? {
              discountPercent: safeNum(form.flashDiscountPercent, 0),
              startAt: form.flashStartAt ? new Date(form.flashStartAt).toISOString() : new Date().toISOString(),
              endAt: form.flashEndAt ? new Date(form.flashEndAt).toISOString() : undefined,
            }
          : undefined,
        flashDealOrder:
          form.isFlashDeal && form.flashDealOrder !== ""
            ? safeNum(form.flashDealOrder)
            : undefined,
        promoLabel: form.promoLabel.trim() || undefined,
      };

      let saved;
      if (isEdit) {
        const res = await updateProduct(product._id, payload);
        saved = res.data?.data ?? res.data ?? { ...product, ...payload };
      } else {
        const res = await createProduct(payload);
        saved = res.data?.data ?? res.data;
      }

      showAlert(isEdit ? "Product updated" : "Product created", "success");
      onSaved(saved, isEdit);
      onClose();
    } catch (err) {
      showAlert(
        err?.response?.data?.message ?? "Failed to save product",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { bgcolor: "background.paper" } }}
    >
      {/* Header */}
      <Box
        sx={{
          background: BRAND_GRADIENT,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>
          {isEdit ? `Edit: ${product.title}` : "Add New Product"}
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{ color: "rgba(255,255,255,0.8)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2.5}>
          {/* Left column */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
            >
              Product Images
            </Typography>
            <Box sx={{ mt: 1 }}>
              <MultiImageUploadArea
                images={form.images}
                onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))}
                onUpload={handleUpload}
                uploading={uploading}
              />
            </Box>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {/* Title */}
              <Grid item xs={12}>
                <TextField
                  label="Product Title"
                  value={form.title}
                  onChange={set("title")}
                  fullWidth
                  required
                  error={Boolean(errors.title)}
                  helperText={errors.title}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={set("description")}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Price + Compare Price */}
              <Grid item xs={6}>
                <TextField
                  label="Price (KSh)"
                  type="number"
                  value={form.price}
                  onChange={set("price")}
                  fullWidth
                  required
                  error={Boolean(errors.price)}
                  helperText={errors.price}
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">KSh</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Compare-at Price"
                  type="number"
                  value={form.comparePrice}
                  onChange={set("comparePrice")}
                  fullWidth
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">KSh</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Category */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Category"
                  value={form.categoryId}
                  onChange={set("categoryId")}
                  fullWidth
                >
                  <MenuItem value="">— No Category —</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Stock */}
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Stock Qty"
                  type="number"
                  value={form.stock}
                  onChange={set("stock")}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>

              {/* Low stock threshold */}
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Low Stock Alert"
                  type="number"
                  value={form.lowStockThreshold}
                  onChange={set("lowStockThreshold")}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>

              {/* Barcode */}
              <Grid item xs={6}>
                <TextField
                  label="Barcode"
                  value={form.barcode}
                  onChange={set("barcode")}
                  fullWidth
                />
              </Grid>

              {/* SKU */}
              <Grid item xs={6}>
                <TextField
                  label="SKU"
                  value={form.sku}
                  onChange={set("sku")}
                  fullWidth
                />
              </Grid>

              {/* Active toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={set("isActive")}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {form.isActive ? "Active — visible to customers" : "Inactive — hidden from store"}
                    </Typography>
                  }
                />
              </Grid>

              {/* ── Promotions section ── */}
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                >
                  Promotions & Visibility
                </Typography>
              </Grid>

              {/* Featured toggle */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.featured}
                      onChange={set("featured")}
                      sx={{
                        "& .MuiSwitch-thumb": {
                          bgcolor: form.featured ? "#f59e0b" : undefined,
                        },
                        "& .MuiSwitch-track": {
                          bgcolor: form.featured ? "#fcd34d !important" : undefined,
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {form.featured ? (
                        <StarIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
                      ) : (
                        <StarBorderIcon sx={{ color: "text.disabled", fontSize: 18 }} />
                      )}
                      <Typography variant="body2" fontWeight={600}>
                        Featured Product
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {form.featured && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Featured Order"
                    type="number"
                    value={form.featuredOrder}
                    onChange={set("featuredOrder")}
                    fullWidth
                    size="small"
                    inputProps={{ min: 1 }}
                    helperText="Lower number = higher priority"
                  />
                </Grid>
              )}

              {/* Flash Deal toggle */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isFlashDeal}
                      onChange={set("isFlashDeal")}
                      color="error"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <FlashOnIcon
                        sx={{
                          color: form.isFlashDeal ? "#ef4444" : "text.disabled",
                          fontSize: 18,
                        }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        Flash Deal
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {form.isFlashDeal && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Discount %"
                      type="number"
                      value={form.flashDiscountPercent}
                      onChange={set("flashDiscountPercent")}
                      fullWidth
                      size="small"
                      inputProps={{ min: 1, max: 99, step: 1 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Deal Order"
                      type="number"
                      value={form.flashDealOrder}
                      onChange={set("flashDealOrder")}
                      fullWidth
                      size="small"
                      inputProps={{ min: 1 }}
                      helperText="Lower = higher priority"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Starts At"
                      type="datetime-local"
                      value={form.flashStartAt}
                      onChange={set("flashStartAt")}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Ends At"
                      type="datetime-local"
                      value={form.flashEndAt}
                      onChange={set("flashEndAt")}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}

              {/* Promo Label */}
              <Grid item xs={12} sm={form.featured && !form.isFlashDeal ? 6 : 12}>
                <TextField
                  label="Promo Badge"
                  value={form.promoLabel}
                  onChange={set("promoLabel")}
                  fullWidth
                  size="small"
                  placeholder="e.g. NEW, HOT, -20%, Bundle"
                  helperText="Short label shown as a badge on the product"
                  inputProps={{ maxLength: 20 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalOfferIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || uploading}
          sx={{ background: BRAND_GRADIENT, minWidth: 120 }}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Product"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── DeleteDialog ───────────────────────────────────────────────────────── */
function DeleteDialog({ open, title, onClose, onConfirm, busy }) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: "background.paper" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningAmberIcon color="error" />
        Delete Product
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete{" "}
          <strong>{title}</strong>? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
        >
          {busy ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── BulkDeleteDialog ───────────────────────────────────────────────────── */
function BulkDeleteDialog({ open, count, onClose, onConfirm, busy }) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { bgcolor: "background.paper" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <DeleteSweepIcon color="error" />
        Bulk Delete
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Permanently delete <strong>{count} products</strong>? This cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <DeleteSweepIcon />}
        >
          {busy ? "Deleting…" : `Delete ${count}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─── ProductCard (Grid view) ────────────────────────────────────────────── */
function ProductCard({ product, selected, onSelect, onEdit, onDelete, onToggleFeatured }) {
  const theme = useTheme();
  const chip = stockChip(product.stock, product.lowStockThreshold);
  const imgSrc = product.images?.[0];

  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
        border: `1px solid ${
          selected
            ? theme.palette.primary.main
            : theme.palette.divider
        }`,
        boxShadow: selected
          ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
          : "none",
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
        },
      }}
    >
      {/* Image */}
      <Box sx={{ position: "relative" }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={onSelect}
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 1,
            bgcolor: "background.paper",
            borderRadius: 1,
            p: 0.3,
          }}
        />

        {/* Featured star (top-right) */}
        <Tooltip title={product.featured ? "Remove from featured" : "Mark as featured"}>
          <IconButton
            size="small"
            onClick={onToggleFeatured}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.35)",
              color: product.featured ? "#fbbf24" : "rgba(255,255,255,0.7)",
              p: 0.4,
              "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            }}
          >
            {product.featured ? (
              <StarIcon sx={{ fontSize: 16 }} />
            ) : (
              <StarBorderIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>

        {imgSrc ? (
          <CardMedia
            component="img"
            height="180"
            image={imgSrc}
            alt={product.title}
            sx={{ objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              height: 180,
              bgcolor: "background.default",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ImageIcon sx={{ fontSize: 48, color: "text.disabled" }} />
          </Box>
        )}

        {/* Flash Deal ribbon */}
        {product.isFlashDeal && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: -22,
              bgcolor: "#ef4444",
              color: "#fff",
              px: 3,
              py: 0.3,
              fontSize: 10,
              fontWeight: 800,
              transform: "rotate(-45deg)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              gap: 0.3,
              letterSpacing: 0.5,
            }}
          >
            <FlashOnIcon sx={{ fontSize: 11 }} />
            FLASH
          </Box>
        )}

        {/* Promo badge */}
        {product.promoLabel && (
          <Chip
            icon={<LocalOfferIcon sx={{ fontSize: "12px !important" }} />}
            label={product.promoLabel}
            size="small"
            color="warning"
            sx={{
              position: "absolute",
              top: 8,
              left: product.isFlashDeal ? 42 : 36,
              height: 20,
              fontSize: 10,
              fontWeight: 800,
              "& .MuiChip-label": { px: 0.6 },
            }}
          />
        )}

        {/* Stock badge */}
        <Chip
          label={chip.label}
          color={chip.color}
          size="small"
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            fontWeight: 700,
            fontSize: 11,
          }}
        />
      </Box>

      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          noWrap
          sx={{ cursor: "pointer", "&:hover": { color: "primary.main" } }}
          onClick={onEdit}
        >
          {product.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {product.category?.name ?? "No Category"}
        </Typography>
        <Box sx={{ mt: 1, display: "flex", alignItems: "baseline", gap: 1 }}>
          {product.isFlashDeal && product.flashDeal?.discountPercent > 0 ? (
            <>
              <Typography variant="body2" fontWeight={800} color="error.main">
                {formatKSh(product.price * (1 - product.flashDeal.discountPercent / 100))}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ textDecoration: "line-through" }}
              >
                {formatKSh(product.price)}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" fontWeight={800} color="primary.main">
                {formatKSh(product.price)}
              </Typography>
              {product.comparePrice > 0 && product.comparePrice > product.price && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ textDecoration: "line-through" }}
                >
                  {formatKSh(product.comparePrice)}
                </Typography>
              )}
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Stock: {safeNum(product.stock)}
        </Typography>
      </CardContent>

      <CardActions sx={{ pt: 0, justifyContent: "flex-end" }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit} color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function ManageProducts() {
  const { showAlert } = useSnackbar();
  const theme = useTheme();

  /* ── data state ── */
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ── filter / sort / view state ── */
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("title");
  const [sortDir, setSortDir] = useState("asc");
  const [view, setView] = useState("table"); // "table" | "grid"

  /* ── pagination ── */
  const [page, setPage] = useState(1); // 1-indexed (matches MUI Pagination)
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [meta, setMeta] = useState(null);

  /* ── debounced search ── */
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  /* ── selection ── */
  const [selected, setSelected] = useState([]);

  /* ── dialog state ── */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);

  /* ── toggle active ── */
  const [togglingId, setTogglingId] = useState(null);

  /* ── inline edit ── */
  const [inlineEdit, setInlineEdit] = useState(null); // { id, field, value }
  const [imgDropTarget, setImgDropTarget] = useState(null);
  const [uploadingImgId, setUploadingImgId] = useState(null);

  /* ─── debounce search input ─────────────────────────────────────────── */
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  /* ─── load data (server-side pagination) ────────────────────────────── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: rowsPerPage };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (statusFilter === "featured") params.featured = "true";
      if (statusFilter === "flash_deal") params.flash = "true";

      const [prodRes, catRes] = await Promise.all([
        api.get("/api/products", { params }),
        fetchCategories(),
      ]);

      const incoming = prodRes.data?.data ?? [];
      setProducts(Array.isArray(incoming) ? incoming : []);
      setMeta(prodRes.data?.meta ?? null);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    } catch {
      showAlert("Failed to load products", "error");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, categoryFilter, statusFilter, showAlert]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /* ─── derived / filtered list ────────────────────────────────────────── */
  // Server handles: search, category, featured, flash_deal
  // Client handles: active/inactive/low_stock/out_of_stock (on current page) + sort
  const filtered = useMemo(() => {
    let list = [...products];

    if (statusFilter === "active") list = list.filter((p) => p.isActive !== false);
    else if (statusFilter === "inactive") list = list.filter((p) => p.isActive === false);
    else if (statusFilter === "low_stock")
      list = list.filter((p) => getStockStatus(p.stock, p.lowStockThreshold) === "low_stock");
    else if (statusFilter === "out_of_stock")
      list = list.filter((p) => getStockStatus(p.stock, p.lowStockThreshold) === "out_of_stock");

    list.sort((a, b) => {
      let av = a[sortField] ?? "";
      let bv = b[sortField] ?? "";
      if (sortField === "price") { av = safeNum(av); bv = safeNum(bv); }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [products, statusFilter, sortField, sortDir]);

  // Reset to page 1 when server-side filters change
  useEffect(() => { setPage(1); }, [categoryFilter, statusFilter]);

  /* ─── selection helpers ──────────────────────────────────────────────── */
  const allSelected =
    filtered.length > 0 && filtered.every((p) => selected.includes(p._id));

  const someSelected = selected.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) setSelected([]);
    else setSelected(filtered.map((p) => p._id));
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ─── sort handler ───────────────────────────────────────────────────── */
  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  /* ─── CRUD handlers ──────────────────────────────────────────────────── */
  const handleSaved = useCallback((saved, isEdit) => {
    if (!saved) return;
    setProducts((prev) => {
      if (isEdit) {
        return prev.map((p) => (p._id === saved._id ? { ...p, ...saved } : p));
      }
      return [saved, ...prev];
    });
  }, []);

  const openAdd = () => {
    setEditProduct(null);
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setDialogOpen(true);
  };

  const openDelete = (product) => {
    setDeleteTarget(product);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteProduct(deleteTarget._id);
      setProducts((prev) => prev.filter((p) => p._id !== deleteTarget._id));
      setSelected((prev) => prev.filter((id) => id !== deleteTarget._id));
      showAlert("Product deleted", "success");
      setDeleteOpen(false);
      setDeleteTarget(null);
    } catch {
      showAlert("Delete failed", "error");
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteBusy(true);
    try {
      await Promise.all(selected.map((id) => deleteProduct(id)));
      setProducts((prev) => prev.filter((p) => !selected.includes(p._id)));
      showAlert(`Deleted ${selected.length} products`, "success");
      setSelected([]);
      setBulkDeleteOpen(false);
    } catch {
      showAlert("Bulk delete failed (partial)", "error");
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  const handleToggleActive = async (product) => {
    const next = product.isActive === false ? true : false;
    setTogglingId(product._id);
    // optimistic
    setProducts((prev) =>
      prev.map((p) => (p._id === product._id ? { ...p, isActive: next } : p))
    );
    try {
      await updateProduct(product._id, { isActive: next });
    } catch {
      // revert
      setProducts((prev) =>
        prev.map((p) =>
          p._id === product._id ? { ...p, isActive: !next } : p
        )
      );
      showAlert("Failed to update status", "error");
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleFeatured = async (product) => {
    const next = !product.featured;
    setProducts((prev) =>
      prev.map((p) => (p._id === product._id ? { ...p, featured: next } : p))
    );
    try {
      await updateProduct(product._id, { featured: next });
      showAlert(next ? "Marked as featured" : "Removed from featured", "success");
    } catch {
      setProducts((prev) =>
        prev.map((p) => (p._id === product._id ? { ...p, featured: !next } : p))
      );
      showAlert("Failed to update featured status", "error");
    }
  };

  const handleBulkSetFeatured = async (featured) => {
    try {
      await Promise.all(selected.map((id) => updateProduct(id, { featured })));
      setProducts((prev) =>
        prev.map((p) => (selected.includes(p._id) ? { ...p, featured } : p))
      );
      showAlert(
        `${selected.length} products ${featured ? "marked featured" : "unfeatured"}`,
        "success"
      );
    } catch {
      showAlert("Bulk featured update failed", "error");
    }
  };

  const handleBulkSetFlashDeal = async (isFlashDeal) => {
    try {
      await Promise.all(selected.map((id) => updateProduct(id, { isFlashDeal })));
      setProducts((prev) =>
        prev.map((p) => (selected.includes(p._id) ? { ...p, isFlashDeal } : p))
      );
      showAlert(
        `${selected.length} products ${isFlashDeal ? "added to flash deal" : "removed from flash deal"}`,
        "success"
      );
    } catch {
      showAlert("Bulk flash deal update failed", "error");
    }
  };

  /* ─── inline edit handlers ──────────────────────────────────────────── */
  const startInlineEdit = (id, field, value) =>
    setInlineEdit({ id, field, value: String(value ?? "") });

  const cancelInlineEdit = () => setInlineEdit(null);

  const commitInlineEdit = async () => {
    if (!inlineEdit) return;
    const { id, field, value } = inlineEdit;
    const parsed = field === "price" ? safeNum(value) : value.trim();
    if (field === "price" && (parsed <= 0 || !Number.isFinite(parsed))) {
      showAlert("Price must be greater than 0", "error");
      return;
    }
    if (field === "title" && !parsed) {
      showAlert("Title cannot be empty", "error");
      return;
    }
    setInlineEdit(null);
    setProducts((prev) =>
      prev.map((p) => (p._id === id ? { ...p, [field]: parsed } : p))
    );
    try {
      await updateProduct(id, { [field]: parsed });
    } catch {
      showAlert(`Failed to update ${field}`, "error");
      loadAll();
    }
  };

  const handleTableImgDrop = async (e, product) => {
    e.preventDefault();
    setImgDropTarget(null);
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setUploadingImgId(product._id);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post("/api/upload", fd);
      const url = res.data?.url;
      if (!url) throw new Error("No URL");
      const newImages = [url, ...(product.images ?? []).slice(1)];
      await updateProduct(product._id, { images: newImages });
      setProducts((prev) =>
        prev.map((p) => (p._id === product._id ? { ...p, images: newImages } : p))
      );
      showAlert("Image updated", "success");
    } catch {
      showAlert("Image upload failed", "error");
    } finally {
      setUploadingImgId(null);
    }
  };

  /* ─── render ─────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100%", p: { xs: 1.5, sm: 3 } }}>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {meta ? meta.total.toLocaleString() : products.length.toLocaleString()} products
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={loadAll}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={openAdd}
            sx={{ background: BRAND_GRADIENT }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Stats chips */}
      <StatsChips
        products={products}
        statusFilter={statusFilter}
        onFilter={setStatusFilter}
      />

      {/* Toolbar */}
      <Box
        sx={{
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2,
          mb: 2,
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Search */}
        <Box
          sx={{
            flex: "1 1 220px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "background.default",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            px: 1.5,
            py: 0.8,
          }}
        >
          <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
          <InputBase
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            fullWidth
            sx={{ fontSize: 14 }}
          />
          {search && (
            <IconButton size="small" onClick={() => setSearch("")} edge="end">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Category filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c._id} value={c._id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Bulk delete */}
        {selected.length > 0 && (
          <Button
            startIcon={<DeleteSweepIcon />}
            variant="outlined"
            color="error"
            onClick={() => setBulkDeleteOpen(true)}
          >
            Delete {selected.length}
          </Button>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* View toggle */}
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, v) => v && setView(v)}
          size="small"
        >
          <ToggleButton value="table">
            <Tooltip title="Table view">
              <TableRowsIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="grid">
            <Tooltip title="Grid view">
              <GridViewIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Bulk selection info bar */}
      {selected.length > 0 && (
        <Box
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            borderRadius: 1.5,
            px: 2,
            py: 1,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" fontWeight={700} color="primary.main">
            {selected.length} product{selected.length > 1 ? "s" : ""} selected
          </Typography>
          <Button
            size="small"
            onClick={() => setSelected(filtered.map((p) => p._id))}
          >
            Select all {filtered.length}
          </Button>
          <Button size="small" color="inherit" onClick={() => setSelected([])}>
            Clear
          </Button>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Mark all selected as Featured">
            <Button
              size="small"
              startIcon={<StarIcon sx={{ fontSize: 15 }} />}
              onClick={() => handleBulkSetFeatured(true)}
              sx={{ color: "#f59e0b", borderColor: "#f59e0b", fontSize: 12 }}
              variant="outlined"
            >
              Feature
            </Button>
          </Tooltip>
          <Tooltip title="Remove Featured from all selected">
            <Button
              size="small"
              startIcon={<StarBorderIcon sx={{ fontSize: 15 }} />}
              onClick={() => handleBulkSetFeatured(false)}
              sx={{ fontSize: 12 }}
              variant="outlined"
              color="inherit"
            >
              Unfeature
            </Button>
          </Tooltip>
          <Tooltip title="Add all selected to Flash Deal">
            <Button
              size="small"
              startIcon={<FlashOnIcon sx={{ fontSize: 15 }} />}
              onClick={() => handleBulkSetFlashDeal(true)}
              sx={{ fontSize: 12 }}
              variant="outlined"
              color="error"
            >
              Flash Deal
            </Button>
          </Tooltip>
          <Tooltip title="Remove Flash Deal from all selected">
            <Button
              size="small"
              onClick={() => handleBulkSetFlashDeal(false)}
              sx={{ fontSize: 12 }}
              variant="outlined"
              color="inherit"
            >
              Remove Flash
            </Button>
          </Tooltip>
        </Box>
      )}

      {/* ── TABLE VIEW ── */}
      {view === "table" && (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "title"}
                      direction={sortField === "title" ? sortDir : "asc"}
                      onClick={() => handleSort("title")}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "price"}
                      direction={sortField === "price" ? sortDir : "asc"}
                      onClick={() => handleSort("price")}
                    >
                      Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(8)].map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <InventoryIcon
                        sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                      />
                      <Typography color="text.secondary" fontWeight={600}>
                        No products found
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Try adjusting your search or filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((product) => {
                    const chip = stockChip(product.stock, product.lowStockThreshold);
                    const imgSrc = product.images?.[0];
                    const isSel = selected.includes(product._id);

                    return (
                      <TableRow
                        key={product._id}
                        hover
                        selected={isSel}
                        sx={{
                          borderLeft: product.featured ? "3px solid" : "3px solid transparent",
                          borderLeftColor: product.featured ? "primary.main" : "transparent",
                          "&.Mui-selected": {
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                          },
                        }}
                      >
                        {/* Checkbox */}
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isSel}
                            onChange={() => toggleSelect(product._id)}
                          />
                        </TableCell>

                        {/* Thumbnail — Avatar with drag & drop + hover upload */}
                        <TableCell sx={{ py: 1 }}>
                          <Box
                            onDragOver={(e) => { e.preventDefault(); setImgDropTarget(product._id); }}
                            onDragLeave={() => setImgDropTarget(null)}
                            onDrop={(e) => handleTableImgDrop(e, product)}
                            sx={{
                              position: "relative",
                              display: "inline-flex",
                              borderRadius: 1.5,
                              outline: imgDropTarget === product._id
                                ? `2px dashed ${theme.palette.primary.main}`
                                : "2px solid transparent",
                              transition: "outline-color 0.15s",
                              "&:hover .img-upload-overlay": { opacity: 1 },
                            }}
                          >
                            {uploadingImgId === product._id ? (
                              <Avatar
                                variant="rounded"
                                sx={{ width: 44, height: 44, bgcolor: "action.hover" }}
                              >
                                <CircularProgress size={16} />
                              </Avatar>
                            ) : (
                              <Avatar
                                variant="rounded"
                                src={imgSrc}
                                alt={product.title}
                                sx={{ width: 44, height: 44, bgcolor: "action.hover" }}
                              >
                                <ImageIcon sx={{ fontSize: 20, color: "text.disabled" }} />
                              </Avatar>
                            )}

                            {/* Drag-over highlight */}
                            {imgDropTarget === product._id && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  borderRadius: 1.5,
                                  bgcolor: alpha(theme.palette.primary.main, 0.3),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  pointerEvents: "none",
                                }}
                              >
                                <UploadIcon sx={{ color: "primary.main", fontSize: 16 }} />
                              </Box>
                            )}

                            {/* Hover overlay — click to upload */}
                            <Box
                              className="img-upload-overlay"
                              component="label"
                              sx={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: 1.5,
                                bgcolor: "rgba(0,0,0,0.52)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0,
                                transition: "opacity 0.18s",
                                cursor: "pointer",
                              }}
                            >
                              <UploadIcon sx={{ color: "#fff", fontSize: 16 }} />
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                disabled={uploadingImgId === product._id}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = "";
                                  if (file) handleTableImgDrop({ preventDefault: () => {}, dataTransfer: { files: [file] } }, product);
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Title — double-click to edit inline */}
                        <TableCell>
                          {inlineEdit?.id === product._id && inlineEdit?.field === "title" ? (
                            <TextField
                              autoFocus
                              size="small"
                              value={inlineEdit.value}
                              onChange={(e) =>
                                setInlineEdit((prev) => ({ ...prev, value: e.target.value }))
                              }
                              onBlur={commitInlineEdit}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitInlineEdit();
                                if (e.key === "Escape") cancelInlineEdit();
                              }}
                              sx={{ maxWidth: 220 }}
                              inputProps={{ style: { fontWeight: 700, fontSize: 13 } }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              noWrap
                              title="Double-click to rename · Single click to open editor"
                              sx={{
                                maxWidth: 240,
                                cursor: "pointer",
                                "&:hover": { color: "primary.main" },
                              }}
                              onClick={() => openEdit(product)}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                startInlineEdit(product._id, "title", product.title);
                              }}
                            >
                              {product.title}
                            </Typography>
                          )}
                          {product.sku && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              display="block"
                            >
                              SKU: {product.sku}
                            </Typography>
                          )}
                          {/* Badge chips */}
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                            {product.isFlashDeal && (
                              <Chip
                                icon={<FlashOnIcon sx={{ fontSize: "11px !important" }} />}
                                label="Flash"
                                size="small"
                                color="error"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700, "& .MuiChip-label": { px: 0.6 } }}
                              />
                            )}
                            {product.promoLabel && (
                              <Chip
                                icon={<LocalOfferIcon sx={{ fontSize: "11px !important" }} />}
                                label={product.promoLabel}
                                size="small"
                                color="warning"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700, "& .MuiChip-label": { px: 0.6 } }}
                              />
                            )}
                          </Box>
                        </TableCell>

                        {/* Category */}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {product.category?.name ?? "—"}
                          </Typography>
                        </TableCell>

                        {/* Price — click to edit inline */}
                        <TableCell>
                          {inlineEdit?.id === product._id && inlineEdit?.field === "price" ? (
                            <TextField
                              autoFocus
                              size="small"
                              type="number"
                              value={inlineEdit.value}
                              onChange={(e) =>
                                setInlineEdit((prev) => ({ ...prev, value: e.target.value }))
                              }
                              onBlur={commitInlineEdit}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitInlineEdit();
                                if (e.key === "Escape") cancelInlineEdit();
                              }}
                              sx={{ maxWidth: 110 }}
                              inputProps={{ min: 0, step: "0.01", style: { fontWeight: 700, fontSize: 13 } }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start" sx={{ fontSize: 11 }}>
                                    KSh
                                  </InputAdornment>
                                ),
                              }}
                            />
                          ) : (
                            <Tooltip title="Click to edit price">
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ cursor: "pointer", "&:hover": { color: "primary.main" } }}
                                onClick={() =>
                                  startInlineEdit(product._id, "price", product.price)
                                }
                              >
                                {formatKSh(product.price)}
                              </Typography>
                            </Tooltip>
                          )}
                          {product.comparePrice > product.price && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ textDecoration: "line-through" }}
                            >
                              {formatKSh(product.comparePrice)}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Stock */}
                        <TableCell>
                          <Chip
                            label={chip.label}
                            color={chip.color}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: 11 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Qty: {safeNum(product.stock)}
                          </Typography>
                        </TableCell>

                        {/* Active toggle */}
                        <TableCell>
                          <Switch
                            size="small"
                            checked={product.isActive !== false}
                            onChange={() => handleToggleActive(product)}
                            disabled={togglingId === product._id}
                            color="primary"
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                            <Tooltip title={product.featured ? "Remove from featured" : "Mark as featured"}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleFeatured(product)}
                                sx={{ color: product.featured ? "#f59e0b" : "text.disabled" }}
                              >
                                {product.featured ? (
                                  <StarIcon fontSize="small" />
                                ) : (
                                  <StarBorderIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => openEdit(product)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => openDelete(product)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination footer */}
          {!loading && meta && meta.pages > 1 && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}
            >
              <Typography variant="body2" color="text.secondary">
                {`${(meta.page - 1) * rowsPerPage + 1}–${Math.min(meta.page * rowsPerPage, meta.total)} of ${meta.total.toLocaleString()}`}
              </Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <FormControl size="small">
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                    sx={{ fontSize: 13, height: 32 }}
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <MenuItem key={n} value={n}>{n} / page</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Pagination
                  size="small"
                  page={page}
                  count={meta.pages}
                  onChange={(_, p) => setPage(p)}
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root.Mui-selected": {
                      background: BRAND_GRADIENT,
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </Stack>
          )}
        </Box>
      )}

      {/* ── GRID VIEW ── */}
      {view === "grid" && (
        <>
          {loading ? (
            <Grid container spacing={2}>
              {[...Array(8)].map((_, i) => (
                <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
                  <Skeleton
                    variant="rectangular"
                    height={320}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 10, textAlign: "center" }}>
              <InventoryIcon sx={{ fontSize: 56, color: "text.disabled", mb: 1.5 }} />
              <Typography color="text.secondary" fontWeight={600}>
                No products found
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Try adjusting your search or filters
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {filtered.map((product) => (
                  <Grid item key={product._id} xs={12} sm={6} md={4} lg={3}>
                    <ProductCard
                      product={product}
                      selected={selected.includes(product._id)}
                      onSelect={() => toggleSelect(product._id)}
                      onEdit={() => openEdit(product)}
                      onDelete={() => openDelete(product)}
                      onToggleFeatured={() => handleToggleFeatured(product)}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Grid pagination */}
              {meta && meta.pages > 1 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ mt: 2, bgcolor: "background.paper", border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 2, py: 1.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {`${(meta.page - 1) * rowsPerPage + 1}–${Math.min(meta.page * rowsPerPage, meta.total)} of ${meta.total.toLocaleString()}`}
                  </Typography>
                  <Pagination
                    size="small"
                    page={page}
                    count={meta.pages}
                    onChange={(_, p) => setPage(p)}
                    shape="rounded"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        background: BRAND_GRADIENT,
                        color: "#fff",
                      },
                    }}
                  />
                </Stack>
              )}
            </>
          )}
        </>
      )}

      {/* ── Dialogs ── */}
      <ProductDialog
        open={dialogOpen}
        product={editProduct}
        categories={categories}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
        showAlert={showAlert}
      />

      <DeleteDialog
        open={deleteOpen}
        title={deleteTarget?.title}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        busy={deleteBusy}
      />

      <BulkDeleteDialog
        open={bulkDeleteOpen}
        count={selected.length}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        busy={bulkDeleteBusy}
      />
    </Box>
  );
}
